import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useGetItemCodesQuery } from '../../api';
import { useLanguage } from '../../contexts/LanguageContext';

const ItemCodeDropdown = ({ value, onChange, className, disabled, claimId }) => {
    const { getCurrentLanguageId, th } = useLanguage();
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filterValue, setFilterValue] = useState(''); // Custom filter input için
    const debounceTimerRef = useRef(null);

    const languageId = getCurrentLanguageId();

    const { data: itemList = [], isLoading: isLoadingItems } = useGetItemCodesQuery({
        RowCount: 50,
        SearchText: searchText,
        LanguageId: languageId,
        ClaimId: claimId
    });

    const itemOptions = useMemo(() => {
        return itemList.map((item) => ({
            id: item.id,
            itemCode: item.itemCode,
            title: item.title,
            displayValue: `${item.itemCode} - ${item.title}`,
            itemPrice: item.itemPrice,
            retailPrice: item.retailPrice,
            karsanPrice: item.karsanPrice,
            itemPriceCurr: item.itemCurrency
        }));
    }, [itemList]);

    const handleSelectionChange = (e) => {
        setSelectedItem(e.value);

        // Seçim yapıldığında filter'ı temizle
        setFilterValue('');
        setSearchText('');

        // Bekleyen timer varsa iptal et
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        onChange(e.value);
    };

    // Custom filter input onChange - Debounce ile API çağrısı
    const handleFilterInputChange = (value) => {
        // Input'ta hemen görünsün
        setFilterValue(value);

        // Önceki timer'ı iptal et
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // 500ms sonra API çağrısı yap
        debounceTimerRef.current = setTimeout(() => {
            setSearchText(value);
        }, 600);
    };

    // Custom filter template - Dropdown'ın kendi filter'ını değil, bizim controlled input'umuzu kullan
    const filterTemplate = () => (
        <div className="p-dropdown-filter-container">
            <InputText value={filterValue} onChange={(e) => handleFilterInputChange(e.target.value)} className="p-dropdown-filter w-full" placeholder={th('common.search')} autoFocus type="text" inputMode="text" />
            <span className="p-dropdown-filter-icon pi pi-search" />
        </div>
    );

    // Cleanup: Component unmount olduğunda timer'ı temizle
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (selectedItem) {
            if (typeof value === 'string' && selectedItem.itemCode === value) {
                return;
            } else if (typeof value === 'object' && value !== null) {
                if (selectedItem.id === value.id || selectedItem.itemCode === value.itemCode) {
                    return;
                }
            }
        }

        if (itemOptions.length > 0 && value) {
            let found;

            if (typeof value === 'object' && value !== null) {
                found = itemOptions.find((option) => option.id === value.id || option.itemCode === value.itemCode);
            } else if (typeof value === 'string' || typeof value === 'number') {
                found = itemOptions.find((option) => option.id?.toString() === value.toString() || option.itemCode === value);
            }

            if (found) {
                setSelectedItem(found);
            } else if (typeof value === 'string') {
                const newOption = {
                    id: 0,
                    itemCode: value,
                    title: value,
                    displayValue: `${value} - ${value}`
                };
                setSelectedItem(newOption);
            } else if (typeof value === 'object' && value !== null) {
                const itemCode = value.itemCode || '';
                const title = value.title || value.partName || itemCode;
                const newOption = {
                    id: value.id || 0,
                    itemCode: itemCode,
                    title: title,
                    displayValue: `${itemCode} - ${title}`
                };
                setSelectedItem(newOption);
            }
        }
    }, [value, itemOptions]);

    return (
        <Dropdown
            disabled={disabled}
            value={selectedItem}
            options={itemOptions}
            onChange={handleSelectionChange}
            optionLabel="displayValue"
            filter
            filterTemplate={filterTemplate}
            showClear
            className={className}
            placeholder={th('common.itemCodeSelectPlaceholder')}
            loading={isLoadingItems}
            emptyFilterMessage={th('common.noResultsFound')}
            itemTemplate={(option) => (
                <div>
                    <span style={{ fontWeight: 'bold' }}>{option.itemCode}</span> - {option.title}
                </div>
            )}
        />
    );
};

export default ItemCodeDropdown;
